// Pure logic for the reviewer's per-change decisions: what a change is called, where the
// review stands, and whether Approve is allowed (rule 1: every change accepted, none rejected).
// The server enforces the same rule; this mirrors it so the buttons tell the truth before the
// round trip.
import type { ReviewDecision, ServerChange } from './collabTypes';

/** The subset of the editor library's TrackedChangeEntry the rail needs. */
export interface ChangeEntry {
  revisionId: number;
  /** For a picture change: the image's data URL, so the card can show what it is. */
  imageSrc?: string;
  insertionRevisionId?: number;
  type: string;
  text: string;
  /** Where a change with NO words of its own sits — the words of its paragraph or row. Shown so
   *  a reviewer can tell one "Formatting changed" from another; never quoted as the change
   *  itself, because it is not what anybody wrote. */
  context?: string;
  deletedText?: string;
  author: string;
  date?: string;
  /** Document positions of the change: `from` is where the card jumps to. */
  from: number;
  to?: number;
  /** OTHER revision ids this one card stands for. The editor library folds related marks into a
   *  single entry — same-author/date insertions merged, paragraph marks and paragraph/table
   *  property changes absorbed into the edit they sit on — and some of the absorbed entries are
   *  dropped from the list entirely. Those ids exist in the file and the server counts them, so
   *  a card that hides them must also DECIDE them. Declared here because the library's own type
   *  carries it and the cast in useChangeRail.ts would otherwise hide it (QA audit P1, 2026-10-09). */
  coalescedRevisionIds?: number[];
  /** Set only on a change the EDITOR never found — "in the page header". Its card cannot jump
   *  to the change, because the change is not in the part on screen. */
  outsideBody?: string;
}

/**
 * The ids that are ONE edit with this card and cannot be decided apart from it.
 *
 * Only the two halves of a replacement. "Deleted device" and "Added instrument" are a single
 * act: accepting one and rejecting the other leaves a sentence that says neither. So the card
 * decides both, together, and always.
 */
export function weldedIds(entry: ChangeEntry): number[] {
  const ids = [entry.revisionId];
  if (entry.insertionRevisionId != null) ids.push(entry.insertionRevisionId);
  return [...new Set(ids)];
}

/**
 * The ids the library BUNDLED into this card that are really separate edits — a new paragraph
 * somewhere else, a formatting change — joined only because they share an author and a minute.
 *
 * They get their own Accept / Reject. One card's decision used to carry them, so a reviewer who
 * liked the word swap but not the new paragraph had no way to say so: rejecting the card sent
 * the swap back too, and the editor was told the swap was the problem. Sharing a timestamp is
 * not a reason to share a verdict.
 */
export function bundledIds(entry: ChangeEntry): number[] {
  const welded = new Set(weldedIds(entry));
  return [...new Set(entry.coalescedRevisionIds ?? [])].filter((id) => !welded.has(id));
}

/**
 * Every change the reviewer must decide — the editor's own entries, plus a card for any change
 * the server counted that the editor never produced.
 *
 * The count and the cards used to come from two places that could disagree. The server unzips
 * the file and scans EVERY part; the editor renders word/document.xml. A tracked change in a
 * page header was therefore counted and never drawn, and since approval needs every counted
 * change decided, the round deadlocked — the reviewer's only way out was rejecting everything
 * and making the editor redo it (reported from staging, 2026-10-07).
 *
 * Now the cards are derived from the server's list, so a counted change always has one. That
 * holds for a footnote, a footer, or any part nobody has thought of yet: there is no case to
 * add, because there is no second source left to disagree.
 *
 * The editor's entry WINS where it has one — it knows the change's position, its picture and
 * its author, where the server knows only what the XML says.
 *
 * "Has one" means EVERY id the entry stands for, not just its primary. The library folds a
 * deletion and the insertion beside it into one "Replaced X → Y" card, and merges same-author
 * marks together. Matching on the primary id alone made the other halves look missing, so each
 * one got its own card reading "Added Y · not shown in the document above" — directly under the
 * card showing Y, on screen. A word swap is the commonest edit in Word, so most reviews would
 * have carried cards that were simply untrue (QA audit P1, 2026-10-09).
 */
export function withChangesTheEditorMissed(
  entries: readonly ChangeEntry[],
  changes: readonly ServerChange[],
): ChangeEntry[] {
  // weldedIds, NOT everything covered. A change the library merely BUNDLED into a card — joined
  // because it shares an author and a minute, not because it is the same edit — gets a card of
  // its own, numbered in the list like any other. Treating it as "found" hid it inside another
  // card, and a reviewer who was happy with the word swap but not with the new paragraph could
  // not say so.
  const found = new Set(entries.flatMap(weldedIds));
  const missed = changes
    .filter((c) => !found.has(c.revision_id))
    .map((c): ChangeEntry => ({
      revisionId: c.revision_id,
      type: c.kind,
      text: c.text,
      context: c.context ?? '',
      author: '',
      // Sorts after every real entry and never matches a document position, so the "jump to
      // the change" affordances stay off.
      from: -1,
      // The location line is for a change the reviewer genuinely CANNOT see: one in a page
      // header, footer or footnote, which the editor does not render at all.
      //
      // NOT for a body change. Those ARE on screen in the redline — the editor simply folded
      // them into another entry instead of listing them separately. Labelling one "not shown in
      // the document" was false, and beside "shown with another change above" it managed to be
      // self-contradicting too (spotted on screen, 2026-10-09).
      outsideBody: c.in_body ? undefined : (c.location || 'not shown in the document above'),
    }));
  return missed.length === 0 ? [...entries] : [...entries, ...missed];
}

export type DecisionKind = 'accept' | 'reject';

export interface ReviewProgress {
  total: number;
  accepted: number;
  rejected: number;
  open: number;
  /** Rule 1. */
  canApprove: boolean;
  /** Something to send back about: a rejection, or nothing decided at all is NOT enough. */
  hasRejections: boolean;
}

export function reviewProgress(revisionIds: readonly number[], decisions: readonly ReviewDecision[]): ReviewProgress {
  const byId = new Map(decisions.map((d) => [d.revision_id, d.decision]));
  let accepted = 0;
  let rejected = 0;
  for (const id of revisionIds) {
    const d = byId.get(id);
    if (d === 'accept') accepted += 1;
    else if (d === 'reject') rejected += 1;
  }
  const total = revisionIds.length;
  const open = total - accepted - rejected;
  return {
    total, accepted, rejected, open,
    canApprove: total > 0 && rejected === 0 && open === 0,
    hasRejections: rejected > 0,
  };
}

const TYPE_LABEL: Record<string, string> = {
  insertion: 'Added',
  deletion: 'Deleted',
  replacement: 'Replaced',
  paragraphMarkInsertion: 'New paragraph',
  paragraphMarkDeletion: 'Paragraphs joined',
  paragraphPropertiesChanged: 'Formatting changed',
  rowInserted: 'Table row added',
  rowDeleted: 'Table row removed',
  rowPropertiesChanged: 'Table row formatted',
  cellInserted: 'Table cell added',
  cellDeleted: 'Table cell removed',
  cellMerged: 'Table cells merged',
  cellPropertiesChanged: 'Table cell formatted',
  tableInserted: 'Table added',
  tableDeleted: 'Table removed',
  tablePropertiesChanged: 'Table formatted',
};

export function changeLabel(type: string): string {
  return TYPE_LABEL[type] ?? 'Changed';
}

/** True when the change's text is a picture placeholder rather than words. */
export function isPictureChange(entry: Pick<ChangeEntry, 'text' | 'imageSrc'>): boolean {
  if (entry.imageSrc) return true;   // the document itself says so
  return /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(entry.text.trim()) || /^image\d*\.\w+$/i.test(entry.text.trim());
}

const PREVIEW_MAX = 140;

/** The words the card shows: "old → new" for a replacement, else the affected text, trimmed. */
export function changePreview(entry: Pick<ChangeEntry, 'type' | 'text' | 'deletedText'>): string {
  const clip = (s: string) => (s.length > PREVIEW_MAX ? `${s.slice(0, PREVIEW_MAX - 1)}…` : s);
  if (entry.type === 'replacement' && entry.deletedText) {
    return `${clip(entry.deletedText)} → ${clip(entry.text)}`;
  }
  return clip(entry.text);
}

/** What the server is told about the change so the trail reads without the file. */
export function changeTextForRecord(entry: Pick<ChangeEntry, 'type' | 'text' | 'deletedText'>): string {
  return changePreview(entry).slice(0, 500);
}

/** The next change still waiting for a decision after `current`, wrapping to the top; null when
 *  every change has one. Drives "decide, then move on" — a reviewer works down the list, and
 *  making them scroll back for each one is the difference between a tool and a chore. */
export function nextOpenChange(
  entries: readonly ChangeEntry[], decided: ReadonlySet<number>, currentRevisionId: number,
): ChangeEntry | null {
  const i = entries.findIndex((e) => e.revisionId === currentRevisionId);
  const after = entries.slice(i + 1).find((e) => !decided.has(e.revisionId));
  return after ?? entries.find((e) => !decided.has(e.revisionId)) ?? null;
}
